import React, { useRef, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiMinus } from 'react-icons/fi';

import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';

import AampeTooltip from '../shared/AampeTooltip/AampeTooltip';

interface CollapseToggleProps {
    collapsed: boolean;
    onToggle: () => void;
}

const CollapseToggle: React.FC<CollapseToggleProps> = ({
    collapsed,
    onToggle,
}) => {
    const [showChevron, setShowChevron] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        onToggle();
        // Blur the button to remove focus after clicking (only for mouse clicks)
        if (e.detail !== 0) {
            buttonRef.current?.blur();
        }
    };

    return (
        <ToggleContainer>
            <AampeTooltip
                title={collapsed ? 'Expand' : 'Collapse'}
                placement="right"
            >
                <ToggleButton
                    ref={buttonRef}
                    onClick={handleClick}
                    onMouseEnter={() => setShowChevron(true)}
                    onMouseLeave={() => setShowChevron(false)}
                    onFocus={() => setShowChevron(true)}
                    onBlur={() => setShowChevron(false)}
                    aria-label={
                        collapsed ? 'Expand sidebar' : 'Collapse sidebar'
                    }
                    aria-expanded={!collapsed}
                    type="button"
                >
                    {showChevron ? (
                        collapsed ? (
                            <FiChevronRight
                                size={20}
                                strokeWidth={1.5}
                                data-testid="chevron-right"
                            />
                        ) : (
                            <FiChevronLeft
                                size={20}
                                strokeWidth={1.5}
                                data-testid="chevron-left"
                            />
                        )
                    ) : (
                        <FiMinus
                            data-testid="minus-icon"
                            size={25}
                            style={{ transform: 'rotate(90deg)' }}
                            strokeWidth={1.5}
                        />
                    )}
                </ToggleButton>
            </AampeTooltip>
        </ToggleContainer>
    );
};

const ToggleContainer = styled.div({
    position: 'absolute',
    right: '-25px',
    top: 0,
    height: '100%',
    width: '30px',
    zIndex: 1001,
    display: 'flex',
    '& > *': {
        display: 'flex',
        width: '100%',
        height: '100%',
    },
});

const ToggleButton = styled.button({
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    appearance: 'none',
    background: 'transparent',
    border: 0,
    padding: 0,
    margin: 0,
    cursor: 'pointer',
    transition: 'all 200ms ease-in-out',
    color: aampeTheme.colors.bordersWG01,

    '&:hover': {
        color: aampeTheme.colors.textAccessibleWG01,
    },

    '&:focus': {
        color: aampeTheme.colors.textAccessibleWG01,
    },

    '&:focus-visible': {
        outline: `2px solid ${aampeTheme.colors.solidA02 || '#3B82F6'}`,
        outlineOffset: '2px',
        borderRadius: '4px',
        background: 'none',
    },
});

export default CollapseToggle;
